'use client'

import SearchPickerContent from '@trustoo/components/searchPicker/SearchPickerContent'
import SearchPickerDropdownRoot from '@trustoo/components/searchPicker/SearchPickerDropdownRoot'
import SearchInputField from '@trustoo/components/searchPicker/SearchPickerInput'
import SearchPickerOption from '@trustoo/components/searchPicker/SearchPickerOption'
import SearchPickerOptionsList from '@trustoo/components/searchPicker/SearchPickerOptionsList'
import SearchPickerPortal from '@trustoo/components/searchPicker/SearchPickerPortal'
import SearchPickerRoot from '@trustoo/components/searchPicker/SearchPickerRoot'

/**
 * SearchPicker compound component.
 *
 * This component follows SOLID principles to provide a flexible and extensible search picker:
 * - **Single Responsibility**: Each subcomponent has one clear purpose
 * - **Open/Closed**: Open for extension (via custom components), closed for modification
 * - **Liskov Substitution**: Components can be replaced with custom implementations
 * - **Interface Segregation**: Minimal, focused interfaces via context
 * - **Dependency Inversion**: Components depend on context abstraction, not concrete implementations
 *
 * The compound structure allows you to compose the picker as needed. You can use the provided
 * components or create custom ones using the `useSearchPickerContext` hook to access shared state.
 *
 * @example
 * Basic usage with default components:
 * ```tsx
 * <SearchPicker.Root
 *   fetchFunction={fetchFunction}
 *   selectedOption={selectedOption}
 *   onSelectOption={onSelectOption}
 * >
 *   <SearchPicker.Content>
 *     <SearchPicker.Input />
 *     <SearchPicker.DropdownRoot>
 *       <SearchPicker.OptionsList />
 *     </SearchPicker.DropdownRoot>
 *   </SearchPicker.Content>
 * </SearchPicker.Root>
 * ```
 * @example
 * Usage with controlled state:
 * ```tsx
 * <SearchPicker.Root
 *   fetchFunction={fetchFunction}
 *   selectedOption={selectedOption}
 *   onSelectOption={onSelectOption}
 *   isOpen={isOpen}
 *   onIsOpenChange={onIsOpenChange}
 * >
 *   <SearchPicker.Content>
 *     <SearchPicker.Input />
 *     <SearchPicker.DropdownRoot>
 *       <SearchPicker.OptionsList />
 *     </SearchPicker.DropdownRoot>
 *   </SearchPicker.Content>
 * </SearchPicker.Root>
 * ```
 *
 * @example
 * Responsive picker (mobile portal, desktop dropdown):
 * ```tsx
 * import ResponsivePickerContent from '@trustoo/components/searchPicker/ResponsivePickerContent'
 *
 * <SearchPicker.Root
 *   fetchFunction={fetchFunction}
 *   selectedOption={selectedOption}
 *   onSelectOption={onSelectOption}
 * >
 *   <SearchPicker.Content>
 *     <ResponsivePickerContent inputFieldProps={inputFieldProps} />
 *   </SearchPicker.Content>
 * </SearchPicker.Root>
 * ```
 *
 * Note: ResponsivePickerContent is a custom implementation that combines Input, Portal,
 * and DropdownRoot with responsive behavior. You can create similar custom components
 * using the building blocks provided by SearchPicker.
 */
const SearchPicker = {
    Root: SearchPickerRoot,
    Content: SearchPickerContent,
    Input: SearchInputField,
    DropdownRoot: SearchPickerDropdownRoot,
    Option: SearchPickerOption,
    OptionsList: SearchPickerOptionsList,
    Portal: SearchPickerPortal,
}

export default SearchPicker
